import { Avatar, Box, Button, Flex, FormControl, FormLabel, SimpleGrid, Spinner, useColorModeValue, useToast } from "@chakra-ui/react";
import { translate } from "i18n";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import FormField from "../../components/fields/FormField";
import PageCard from "../../components/page-card";
import SegmentedControl from "../../components/segmented-control";
import { CoachPricingRuleDto } from "../../models/coach/dto/coach-pricing-rule.dto";
import { UpdateCoachInputDto } from "../../models/coach/dto/update-coach.dto";
import { SportType } from "../../models/court/dto/court.dto";
import { typography } from 'theme/tokens';
import { paramFromWindowLocation } from "../../utils/url-parser";
import CoachPricingEditor from "../coach-pricing-management/components/CoachPricingEditor";
import SportSelect from "components/SportSelect";
import ErrorState from "../../components/states/ErrorState";
import InlineErrorState from "../../components/states/InlineErrorState";
import { CoachCalendar } from "./CoachCalendarPage";
import AvatarUpload from "./components/CoachAvatarUploader";
import CoachContactFields from "./components/CoachContactFields";
import { CoachContactDraft, coachContactDraftFrom, coachContactPayload } from "./utils/coach-contact";
import { useIsOrgManager } from "../auth/roles";
import { useGetCoach } from "./hooks/useGetCoach";
import { useGetCoachAdmin } from "./hooks/useGetCoachAdmin";
import { useCanManageCoach } from "./hooks/useCanManageCoach";
import { useUpdateCoach } from "./hooks/useUpdateCoach";

enum ViewMode { details = 'details', calendar = 'calendar' };
export default function CoachDetailsPage() {
    const [viewMode, setViewMode] = useState<ViewMode>(ViewMode.details);
    const coachId = paramFromWindowLocation('coach');
    // `GET coachs/:id` can answer 403 (an operator who is no longer an admin of
    // the selected org, or a coach opening another coach's row), 404 (a deleted
    // coach, or a stale bookmark) and 5xx. React Query leaves `coach`
    // undefined for every one of them, and this page renders on `coach &&` —
    // so a refused read used to render the view toggle and NOTHING ELSE,
    // indefinitely, indistinguishable from a read still in flight and from a
    // URL carrying no `?coach=` at all.
    const {
        data: coach,
        isLoading: isCoachLoading,
        isError: isCoachError,
        refetch: refetchCoach,
    } = useGetCoach(coachId);
    // Only a genuinely in-flight read spins, so no state here is an unbounded
    // spinner (R83's game-details defect). The query is `enabled: Boolean(id)`,
    // and React Query v4 reports a DISABLED query as `isLoading: true` with
    // `fetchStatus: 'idle'` — so a URL carrying no `?coach=` would otherwise
    // spin for as long as the operator left the tab open. Everything that is
    // not pending and has no coach is a failed read, including a 200 with an
    // empty body.
    const coachPending = Boolean(coachId) && !isCoachError && isCoachLoading;
    // Admins manage every coach; a coach manages only their OWN profile and
    // availabilities (API: hasCoachSelfOrOrganizationAdminAccess). The
    // own-coach lookup only runs for coach roles.
    const isOrgManager = useIsOrgManager();
    const canManageThisCoach = useCanManageCoach(coachId);
    const [hasBeenEdited, setHasBeenEdited] = useState(false);
    const [pricingRulesEdited, setPricingRulesEdited] = useState(false);
    // MYS-75 — the contact fields (phone, public toggle, notification email)
    // are ADMIN-only on this page. Their current values come from the
    // admin-only `GET coachs/:id/admin` (D1), and only an admin may set the
    // notification email (D2). A coach opening their own profile keeps the
    // pre-MYS-75 form: whether they may read and edit their own private phone
    // is blocked question Q1 in the MYS-75 ledger, and nothing here answers it.
    const {
        data: coachAdmin,
        isError: isCoachAdminError,
        refetch: refetchCoachAdmin,
    } = useGetCoachAdmin(coachId, isOrgManager);
    const [contact, setContact] = useState<CoachContactDraft | null>(null);
    const [contactEdited, setContactEdited] = useState(false);
    const toast = useToast();
    const labelColor = useColorModeValue('secondaryGray.900', 'white');
    const { mutateAsync: updateCoach, isLoading: isUpdatingCoach } = useUpdateCoach(coachId, () => {
        toast({
            title: translate('coachs.coach_edited_successfully'),
            status: 'success',
            duration: 3000,
            position: 'top-right'
        });
        setHasBeenEdited(false);
        setPricingRulesEdited(false);
        setContactEdited(false);
    });
    const [editedCoach, setEditedCoach] = useState(() => {
        if (!coach)
            return null;
        return { ...coach };
    });
    const [editedPricingRules, setEditedPricingRules] = useState<CoachPricingRuleDto[]>([]);

    useEffect(() => {
        if (coach && !editedCoach)
            setEditedCoach({ ...coach });
    }, [coach, editedCoach]);

    // Seeded once from the admin read, like `editedCoach`: a background refetch
    // must not overwrite what the admin is typing.
    useEffect(() => {
        if (coachAdmin && !contact)
            setContact(coachContactDraftFrom(coachAdmin));
    }, [coachAdmin, contact]);

    useEffect(() => {
        if (coach && coach.pricingRules) {
            setEditedPricingRules([...coach.pricingRules]);
        }
    }, [coach]);


    // Deep-linkable view mode (`?view=calendar`): the coach onboarding tour
    // (MYS-132) navigates here to spotlight the availability calendar, and the
    // component stays mounted across that search-param change — hence an
    // effect, not just an initial state.
    const location = useLocation();
    useEffect(() => {
        const view = new URLSearchParams(location.search).get('view');
        if (view === ViewMode.calendar || view === ViewMode.details) {
            setViewMode(view as ViewMode);
        }
    }, [location.search]);

    // REMOVED at run 137: a `useDateBoundary(7)` → `useGetWeeklyDates` →
    // `useGetCoachWeeklyCalendar` → `weeklyData` → `calendarData` chain stood
    // here, and `calendarData` had exactly one definition and ZERO uses — the
    // calendar view below renders `<CoachCalendar>`, which fetches its own data.
    // `setDateBoundary` was never called either. The fan-out is `useQueries` over
    // seven dates, so every render of this page issued SEVEN
    // `GET coachs/:id/calendar/...` requests to build a memo nobody read.
    //
    // Found while measuring the `useQueries` population for R137-1, and it is
    // also why the defect this page was SUSPECTED of does not exist: the guess
    // was that a refused weekly read rendered the coach's week as free (the
    // `if (!item)` arm returned an empty day). It cannot, because nothing renders
    // it — D64@r106, and this campaign has now measured such guesses wrong twelve
    // times out of twelve. The hook keeps its other caller-less export; deleting
    // a hook on a census's word is what D67@r110 warns about.

    const onValueChange = useCallback((key: keyof typeof editedCoach, value: any) => {
        setEditedCoach(prev => {
            return { ...prev, [key]: value };
        });
        setHasBeenEdited(true);
    }, [setEditedCoach]);

    const onPricingRulesChange = useCallback((newRules: CoachPricingRuleDto[]) => {
        setEditedPricingRules(newRules);
        setPricingRulesEdited(true);
    }, []);

    const onContactChange = useCallback((next: CoachContactDraft) => {
        setContact(next);
        setContactEdited(true);
    }, []);

    // The contact fields ride the PUT only when an admin edited them. Left out,
    // the API leaves them untouched (MYS-75 T2's tri-state contract), so a
    // failed admin read can never clear a number by sending blanks.
    const contactPayload = contactEdited && contact ? coachContactPayload(contact) : undefined;
    const contactInvalid = contactPayload === null;

    const handleSave = useCallback(async () => {
        if (contactInvalid) return;
        if (hasBeenEdited || pricingRulesEdited || contactEdited) {
            const updateData: UpdateCoachInputDto = {
                firstName: editedCoach.firstName,
                lastName: editedCoach.lastName,
                avatar: editedCoach.avatar,
                sports: editedCoach.sports ?? [],
                pricingRules: pricingRulesEdited ? editedPricingRules : undefined,
                ...(contactPayload ?? {}),
            };
            try {
                await updateCoach(updateData);
            } catch (error) {
                toast({
                    title: translate('error'),
                    description: (error as Error)?.message || translate('error'),
                    status: 'error',
                    duration: 5000,
                    position: 'top-right'
                });
            }
        }
    }, [hasBeenEdited, pricingRulesEdited, contactEdited, contactInvalid, contactPayload, editedCoach, editedPricingRules, updateCoach, toast]);

    return (
        <>
            <Box w={'100%'} display='flex' justifyContent={'flex-end'} mb={4} data-tour="coach-view-toggle">
                <SegmentedControl
                    options={[{ id: ViewMode.details, label: translate('tabs.coachs') }, { id: ViewMode.calendar, label: translate('court.calendar') }]}
                    selectedId={viewMode}
                    onChange={(id) => {
                        setViewMode(id as ViewMode);
                    }}
                />
            </Box>
            {/* Stale-but-true stays on screen: React Query keeps `coach` when a
                BACKGROUND refetch fails and raises `isError` beside it, and
                blanking a correct page because a refresh failed is the mirror
                of the defect this fixes (R96-3). */}
            {!coach && coachPending && (
                <Flex justifyContent="center" py={10}>
                    <Spinner size="lg" />
                </Flex>
            )}
            {!coach && !coachPending && (
                <ErrorState
                    message={translate('common.unexpected_error')}
                    onRetry={coachId ? () => { refetchCoach(); } : undefined}
                />
            )}
            {coach && viewMode === ViewMode.details && <PageCard showBackButton title={translate('tabs.coachs')} rightHeader={canManageThisCoach ? <Button isDisabled={(!hasBeenEdited && !pricingRulesEdited && !contactEdited) || contactInvalid} isLoading={isUpdatingCoach} colorScheme="brand" onClick={handleSave}>
                {translate("save")}
            </Button> : null}>
                <SimpleGrid paddingBottom={'400px'} columns={{ sm: 2, }}>
                    <Box px={6} pb={6}>
                        <FormField label={translate('coachs.firstName')} value={editedCoach?.firstName || ''} onChange={(v: string) => onValueChange('firstName', v)} />
                        <FormField label={translate('coachs.lastName')} value={editedCoach?.lastName || ''} onChange={(v: string) => onValueChange('lastName', v)} />
                        {/* MYS-75: right under the name, as the ticket's screenshot places them and as the create modal does. */}
                        {isOrgManager && contact && (
                            <CoachContactFields value={contact} onChange={onContactChange} isDisabled={isUpdatingCoach} />
                        )}
                        {isOrgManager && !contact && isCoachAdminError && (
                            <Box my={4}>
                                <InlineErrorState message={translate('coachs.contact_load_error')} />
                                <Button size="sm" mt={2} onClick={() => { refetchCoachAdmin(); }}>
                                    {translate('coachs.contact_retry')}
                                </Button>
                            </Box>
                        )}
                        <FormControl><FormLabel {...typography.formLabel} color={labelColor} mb="6px">{translate('event.sport')}</FormLabel>
                            <SportSelect
                                value={editedCoach?.sports as SportType[] || []}
                                onChange={newValue => {
                                    onValueChange('sports', newValue)
                                }}
                            />
                        </FormControl>
                        <FormControl>
                            <FormLabel {...typography.formLabel}>{translate('coachs.pricing_rules')}</FormLabel>
                            <CoachPricingEditor
                                title=""
                                coach={editedCoach || coach}
                                pricingRules={editedPricingRules}
                                setPricingRules={onPricingRulesChange}
                            />
                        </FormControl>
                    </Box>
                    {/* Lockstep: `POST /coachs/:id/avatar` is guarded by
                        hasCoachSelfOrOrganizationAdminAccess, so for a coach
                        looking at ANOTHER coach the upload button was a button
                        the API rejects. Show the avatar read-only instead. */}
                    {canManageThisCoach ? (
                        <AvatarUpload
                            coachId={coachId}
                            avatar={coach?.avatar}
                            fullName={`${coach?.firstName} ${coach?.lastName}`}
                        />
                    ) : (
                        <Box textAlign="center">
                            <Avatar
                                src={coach?.avatar}
                                name={`${coach?.firstName} ${coach?.lastName}`}
                                size="2xl"
                                my={4}
                            />
                        </Box>
                    )}
                </SimpleGrid>
            </PageCard>}
            <Box h={4} />
            {coach && viewMode === ViewMode.calendar && <Box data-tour="coach-availabilities"><CoachCalendar coachId={coach.id} canManage={canManageThisCoach} /></Box>}
        </>
    )
}
